import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { setAuthToken } from '../lib/tokenStorage';
import { trackGAEvent } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Handles the return trip from https://auth.emergentagent.com/
 * The URL contains a fragment like `#session_id=<uuid>` which we exchange
 * for our own JWT via the backend, then navigate to the homepage.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Guard against React StrictMode double-invocation
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : '';

    if (!sessionId) {
      setError('Missing session_id — please try signing in again.');
      setTimeout(() => navigate('/auth', { replace: true }), 2500);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/google/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.detail || 'Sign-in failed');
        }
        // Persist JWT so downstream requests work identically to email/pw login
        setAuthToken(data.token);
        // Beacon transport so the event survives the full-page navigation below
        trackGAEvent('login', { method: 'google', transport_type: 'beacon' });
        // Strip the fragment so subsequent reloads don't re-process
        window.history.replaceState({}, document.title, '/');
        // Full navigation so AuthProvider re-runs and picks up the new token
        window.location.replace('/');
      } catch (e) {
        setError(e?.message || 'Sign-in failed');
        setTimeout(() => navigate('/auth', { replace: true }), 2500);
      }
    })();
  }, [navigate]);

  return (
    <main
      className="min-h-screen bg-black flex items-center justify-center px-6"
      data-testid="auth-callback-page"
    >
      <div className="text-center max-w-md">
        {error ? (
          <>
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-4" />
            <p className="text-white text-lg mb-2" data-testid="auth-callback-error">
              {error}
            </p>
            <p className="text-zinc-500 text-sm">Redirecting back to sign-in…</p>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 text-[#C7FA3E] animate-spin mx-auto mb-4" />
            <p className="text-white text-lg mb-2" data-testid="auth-callback-loading">
              Signing you in…
            </p>
            <p className="text-zinc-500 text-sm">Just a moment while we verify your Google account.</p>
          </>
        )}
      </div>
    </main>
  );
}
